import {
    computed,
    DestroyRef,
    inject,
    Injectable,
    Signal,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { IMessage } from '@dxp/core/models';
import { TranslationApiService } from '@dxp/core/api';

interface ITranslationInformation {
    enabled: boolean;
    channelId: number;
    localLangauge: string;
}

@Injectable({
    providedIn: 'root',
})
export class TranslationService {
    readonly #destroyRef = inject(DestroyRef);
    readonly #translationApiService = inject(TranslationApiService);

    readonly #translation = signal(new Map<string, ITranslationInformation>());

    settings(
        conversationId: string,
    ): Signal<ITranslationInformation | undefined> {
        return computed(() => this.#translation().get(conversationId));
    }

    addConversation(conversationId: string, channelId: number) {
        this.#translationApiService
            .getSettings()
            .pipe(
                tap(settings => {
                    const enabled = !!(
                        settings.featureAvailable && settings.autoTranslate
                    );
                    this.#setTranslation(conversationId, {
                        enabled,
                        channelId,
                        localLangauge: 'EN-GB',
                    });
                }),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe();
    }

    removeConversation(conversationId: string) {
        this.#translation.update(prev => {
            const next = new Map(prev);
            next.delete(conversationId);
            return next;
        });
    }

    toggleTranslation(conversationId: string, translate: boolean) {
        const previous = this.#translation().get(conversationId);

        this.#translation.update(prev => {
            const next = new Map(prev);
            next.set(conversationId, {
                enabled: translate,
                channelId: previous?.channelId ?? 0,
                localLangauge: previous?.localLangauge ?? 'EN-GB',
            });
            return next;
        });
    }

    translateFromForeign$(
        messages: IMessage[],
        conversationId: string,
    ): Observable<IMessage[]> {
        const settings = this.#translation().get(conversationId);

        if (!settings?.enabled || messages.length === 0) {
            return of(messages);
        }

        return this.#translationApiService
            .translateFromForeign(
                settings.channelId,
                settings.localLangauge,
                messages.map(m => m.textMessage ?? ''),
            )
            .pipe(
                map(({ translations }) => {
                    const len = Math.min(translations.length, messages.length);

                    const updated = messages.map((m, i) =>
                        i < len
                            ? {
                                  ...m,
                                  translatedText: translations[i].text,
                                  detectedLanguage:
                                      translations[i].detected_source_language,
                              }
                            : m,
                    );
                    return updated;
                }),
                catchError(_ => {
                    return of(messages);
                }),
            );
    }

    translateToForeign$(
        messages: IMessage[],
        textToSend: string,
        conversationId: string,
    ): Observable<string> {
        const settings = this.#translation().get(conversationId);

        if (
            !settings?.enabled ||
            messages.length === 0 ||
            textToSend.length === 0
        ) {
            return of(textToSend);
        }

        const detectForeignLanguage = this.#detectLanguage(
            messages
                .filter(
                    m => m.sender.role === 'Customer' && !!m.detectedLanguage,
                )
                .map(m => m.detectedLanguage!),
        );

        return this.#translationApiService
            .translateToForeign(
                settings.channelId,
                settings.localLangauge,
                detectForeignLanguage ?? settings.localLangauge,
                [textToSend],
            )
            .pipe(
                map(({ translations }) => {
                    return translations[0].text;
                }),
                catchError(_ => {
                    return of(textToSend);
                }),
            );
    }

    #setTranslation(conversationId: string, info: ITranslationInformation) {
        this.#translation.update(prev => {
            const next = new Map(prev);
            next.set(conversationId, info);
            return next;
        });
    }

    #detectLanguage(languages: string[]): string | undefined {
        return languages
            .sort(
                (a, b) =>
                    languages.filter(v => v === a).length -
                    languages.filter(v => v === b).length,
            )
            .pop();
    }
}
